/**
 * Error Boundary Components
 *
 * React error boundaries for graceful error handling in the simulator.
 * Provides user-friendly error messages in Polish with recovery options.
 */

import { Component, type ReactNode, type ErrorInfo } from 'react';
import {
  Alert,
  Button,
  Group,
  Stack,
  Text,
  Paper,
  Code,
} from '@mantine/core';
import { IconAlertTriangle, IconRefresh, IconHome } from '@tabler/icons-react';

// =============================================================================
// GENERIC ERROR BOUNDARY
// =============================================================================

type FallbackRender = (
  error: Error | null,
  errorInfo: ErrorInfo | null
) => ReactNode;

export interface ErrorBoundaryProps {
  /** Children to render */
  children: ReactNode;
  /** Fallback UI or render function */
  fallback: ReactNode | FallbackRender;
  /** Callback when error occurs */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Keys that trigger reset when changed */
  resetKeys?: unknown[];
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Generic Error Boundary component
 *
 * Catches JavaScript errors in child components and displays fallback UI.
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Reset error state when resetKeys change
    if (
      this.state.hasError &&
      this.props.resetKeys &&
      prevProps.resetKeys &&
      !arraysEqual(prevProps.resetKeys, this.props.resetKeys)
    ) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
      });
    }
  }

  render(): ReactNode {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Support render prop pattern
      if (typeof fallback === 'function') {
        return (fallback as FallbackRender)(error, errorInfo);
      }
      return fallback;
    }

    return children;
  }
}

// Helper function to compare arrays
function arraysEqual(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((val, idx) => val === b[idx]);
}

// =============================================================================
// SIMULATOR ERROR BOUNDARY
// =============================================================================

export interface SimulatorErrorBoundaryProps {
  /** Children to render */
  children: ReactNode;
  /** Callback when user clicks reset */
  onReset?: () => void;
  /** Callback when user clicks retry */
  onRetry?: () => void;
  /** Show technical error details (for development) */
  showDetails?: boolean;
}

interface SimulatorErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showTechnicalDetails: boolean;
}

/**
 * Simulator-specific Error Boundary
 *
 * Provides Polish-language error messages and recovery options
 * specifically designed for the Energy Certificate Simulator.
 *
 * @example
 * ```tsx
 * <SimulatorErrorBoundary onReset={resetSimulator}>
 *   <EnergyCertificateSimulator />
 * </SimulatorErrorBoundary>
 * ```
 */
export class SimulatorErrorBoundary extends Component<
  SimulatorErrorBoundaryProps,
  SimulatorErrorBoundaryState
> {
  constructor(props: SimulatorErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showTechnicalDetails: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<SimulatorErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    // Log error for debugging
    console.error('SimulatorErrorBoundary caught error:', error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    this.props.onRetry?.();
  };

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    this.props.onReset?.();
  };

  toggleDetails = (): void => {
    this.setState(prev => ({
      showTechnicalDetails: !prev.showTechnicalDetails,
    }));
  };

  render(): ReactNode {
    const { hasError, error, errorInfo, showTechnicalDetails } = this.state;
    const { children, showDetails } = this.props;

    if (!hasError) {
      return children;
    }

    const isDevelopment = process.env.NODE_ENV === 'development';
    const shouldShowDetails = showDetails || (isDevelopment && showTechnicalDetails);

    return (
      <Paper p="xl" withBorder aria-live="assertive">
        <Stack gap="lg" align="center">
          <Alert
            icon={<IconAlertTriangle size={24} />}
            title="Wystąpił błąd"
            color="red"
            variant="light"
            w="100%"
            role="alert"
          >
            <Stack gap="sm">
              <Text>
                Przepraszamy, wystąpił nieoczekiwany błąd podczas działania symulatora.
                Prosimy spróbować ponownie lub zacząć od nowa.
              </Text>

              {isDevelopment && (
                <Button
                  variant="subtle"
                  size="xs"
                  onClick={this.toggleDetails}
                  c="dimmed"
                >
                  {showTechnicalDetails ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}
                </Button>
              )}

              {shouldShowDetails && (
                <Paper p="sm" bg="gray.1" mt="sm">
                  <Stack gap="xs">
                    <Text size="sm" fw={600}>
                      Szczegóły błędu:
                    </Text>
                    <Code block>{error?.message || 'Unknown error'}</Code>
                    {errorInfo?.componentStack && (
                      <>
                        <Text size="sm" fw={600}>
                          Stos komponentów:
                        </Text>
                        <Code block style={{ fontSize: '0.75rem', maxHeight: '200px', overflow: 'auto' }}>
                          {errorInfo.componentStack}
                        </Code>
                      </>
                    )}
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Alert>

          <Group>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="light"
              onClick={this.handleRetry}
            >
              Spróbuj ponownie
            </Button>
            <Button
              leftSection={<IconHome size={16} />}
              variant="outline"
              onClick={this.handleReset}
            >
              Zacznij od nowa
            </Button>
          </Group>

          <Text size="sm" c="dimmed" ta="center">
            Jeśli problem się powtarza, odśwież stronę lub skontaktuj się z nami.
          </Text>
        </Stack>
      </Paper>
    );
  }
}

// =============================================================================
// EXPORTS
// =============================================================================

export default ErrorBoundary;
